import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { base, get_invoice, confirm_payment, regenerate } from "../../constants/urls";
import { useAuth } from "../../store/Auth";
import Swal from "sweetalert2";
import { useFormStep } from "../../store/FormStepStore";
import { useInvoiceStore } from "../../store/InvoiceStore";
import { formatDateNew } from "../../utils/date_format";
import Loader from "../Loader";
import { generate_trans_ref } from "../../utils/generate_trans_ref";

export const COLUMNS = [
  {
    Header: "S/No.",
    accessor: "serial",
  },
  {
    Header: "Request Date",
    accessor: "date",
    Cell: ({ row }) => {
      const data = row.original;
      return <p>{formatDateNew(data.date)}</p>;
    },
  },
  {
    Header: "Request Number",
    accessor: "request_number",
  },
  {
    Header: "Request Type",
    accessor: "request_type",
  },
  {
    Header: "Licence Plate",
    accessor: "license_plate",
  },
  {
    Header: "Vehicle Make/Model",
    accessor: "make",
  },
  {
    Header: "Invoice/Receipt Number",
    accessor: "invoice_number",
    Cell: ({ row }) => {
      const navigate = useNavigate();
      const data = row.original;

      const handleRouting = (e) => {
        e.stopPropagation();
        data.payment_status === "Paid"
          ? navigate(`/receipt/${data.reference}?source=requests`)
          : navigate(`/invoice/${data.reference}`);
      };

      return (
        <div className="flex flex-wrap space-x-2" onClick={handleRouting}>
          <span>
            {data.payment_status === "Paid"
              ? "CR" + data.invoice_number
              : "CI" + data.invoice_number}
          </span>
          <span className="text-xs py-1 bg-gray-600 text-white items-center rounded-lg cursor-pointer px-2">
            View
          </span>
        </div>
      );
    },
  },
  {
    Header: "Payment Status",
    accessor: "payment_status",
  },
  {
    Header: "ID Validation",
    accessor: "owner_status",
    Cell: ({ row }) => {
      const data = row.original;
      return (
        <div
          style={{
            color:
              data.owner_status === "Verified"
                ? "green"
                : data.owner_status === "Not Verified"
                ? "orange"
                : "red",
          }}
        >
          {data.owner_status}
        </div>
      );
    },
  },
  {
    Header: "Vehicle Status",
    accessor: "vehicle_status",
    Cell: ({ row }) => {
      const data = row.original;
      return (
        <div
          style={{
            color:
              data.vehicle_status === "Valid"
                ? "green"
                : data.vehicle_status === "Conflict"
                ? "orange"
                : "red",
          }}
        >
          {data.vehicle_status}
        </div>
      );
    },
  },
  {
    Header: "Request Status",
    accessor: "request_status",
    Cell: ({ row }) => {
      const data = row.original;
      return (
        <div
          style={{
            color:
              data.request_status === "Approved"
                ? "green"
                : data.request_status === "Processing"
                ? "orange"
                : "red",
          }}
        >
          {data.payment_status === "Unpaid" ? (
            <span className="py-1 text-red-500">Incomplete</span>
          ) : (
            data.request_status
          )}
        </div>
      );
    },
  },
  {
    Header: "Action",
    accessor: "action",
    Cell: ({ row }) => {
      const data = row.original;
      const setInvoice = useInvoiceStore((state) => state.setInvoiceDetails);
      const setStep = useFormStep((state) => state.setStep);
      const getAuth = useAuth((state) => state.auth);
      
      const [loading, setLoading] = useState(false);
      const [regenerating, setRegenerating] = useState(false);
      const [confirmed, setConfirmed] = useState("");
      
      const navigate = useNavigate();

      // ==========================================
      // ACTION HANDLERS
      // ==========================================

      const handleRequest = (e) => {
        e.stopPropagation();
        navigate(`/viewrequest/${data.reference}`);
      };

      const handleReview = (e) => {
        e.stopPropagation();
        navigate(`/review/${data.vehicle_id}`);
      };

      // FIX: Dynamically route to the correct certificate page based on request type
      const handleViewCertificate = (e) => {
        e.stopPropagation();
        if (data.request_type && data.request_type.includes("Change of Ownership")) {
          navigate(`/cowcert/${data.request_id}`);
        } else {
          navigate(`/cert/${data.request_id}`);
        }
      };

      const getProductType = (product_desc) => {
        let product = "";
        let code = "";
        switch (product_desc) {
          case "Motor Vehicle Information (New)":
            product = "cmrinfo1";
            code = "01";
            break;
          case "Motor Vehicle Information (Renewal)":
            product = "cmrinfo2";
            code = "11";
            break;
          default:
        }
        return [product, code];
      };

      const RegenerateTransaction = async (e, product) => {
        e.preventDefault();
        e.stopPropagation();

        if (data.payment_status === "Paid") {
          Swal.fire("This transaction already paid for");
          return;
        }

        setRegenerating(true);
        let new_reference = product[1] + generate_trans_ref().toString();
        let url = `${base}${regenerate}${product[0]}/${data.reference}/${new_reference}`;
        let token = getAuth.token;

        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then((response) => response.json())
          .then((respJson) => {
            if (respJson.statusCode === 0) {
              let invoiceData = respJson.data.invoice;
              setInvoice({
                firstName: invoiceData.first_name,
                middleName: invoiceData.middle_name,
                lastName: invoiceData.last_name,
                address: invoiceData.address,
                email: invoiceData.email,
                phone: invoiceData.phone,
                narration: invoiceData.narration,
                amount: invoiceData.amount,
                rrr: invoiceData.rrr,
                transactionId: invoiceData.trans_ref,
                invoiceNumber: invoiceData.invoice_number,
                initiatedDate: invoiceData.initiate_date,
                status: "Unpaid",
                productCode: invoiceData.product_code,
                make: invoiceData.make,
                vin: invoiceData.vin,
                licenceNumber: invoiceData.license,
              });
              setStep(6);
              window.location = "/invoice";
            } else {
              Swal.fire("Unable to retrieve transaction. Click Regenerate to resubmit");
            }
          })
          .catch((err) => {
            let message = "Unable to continue, please try again";
            if (err && err.response) {
              message = err.response.data.message;
            }
            Swal.fire(message);
          })
          .finally(() => {
            setRegenerating(false);
          });
      };

      const confirmPayment = async (e) => {
        e.stopPropagation();
        setLoading(true);
        const token = getAuth.token;
        let url = base + get_invoice + `${data.reference}`;

        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then((response) => response.json())
          .then((respJson) => {
            if (respJson.statusCode === 0) {
              let trans = respJson.data.transaction;
              url = `${base}${confirm_payment}cmrinfo1/${data.reference}/${trans.rrr}`;
              
              fetch(url, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: "Bearer " + token,
                },
              })
                .then((response) => response.json())
                .then((resp) => {
                  if (resp.statusCode === 0) {
                    if (resp.data.Status === "01") {
                      Swal.fire({
                        title: "",
                        text: "Payment confirmed",
                        icon: "success",
                        confirmButtonText: "OK",
                        confirmButtonColor: "#1C89DE",
                      }).then(() => {
                        setConfirmed(true);
                      });
                    } else {
                      Swal.fire({
                        title: "Error!",
                        text: "Payment Not Found",
                        icon: "error",
                        confirmButtonText: "OK",
                        confirmButtonColor: "#1C89DE",
                      });
                    }
                  }
                })
                .catch(() => {
                  Swal.fire({
                    title: "Error!",
                    text: "Unable to Confirm Transaction",
                    icon: "error",
                    confirmButtonText: "Ok",
                    confirmButtonColor: "#1C89DE",
                  });
                })
                .finally(() => setLoading(false));
            } else {
              Swal.fire({
                title: "Error!",
                text: "Error processing this invoice",
                icon: "error",
                confirmButtonText: "OK",
                confirmButtonColor: "#1C89DE",
              });
              setLoading(false);
            }
          });
      };

      const handlePayment = async (e) => {
        e.stopPropagation();
        let url = base + get_invoice + `${data.reference}`;
        let token = getAuth.token;

        fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then((response) => response.json())
          .then((respJson) => {
            if (respJson.statusCode === 0) {
              let invoiceData = respJson.data.transaction;
              setInvoice({
                firstName: invoiceData.first_name,
                middleName: invoiceData.middle_name,
                lastName: invoiceData.last_name,
                address: invoiceData.address,
                email: invoiceData.email,
                phone: invoiceData.phone,
                narration: invoiceData.narration,
                amount: invoiceData.amount,
                rrr: invoiceData.rrr,
                transactionId: invoiceData.trans_ref,
                invoiceNumber: invoiceData.invoice_number,
                initiatedDate: invoiceData.initiate_date,
                status: "Unpaid",
                productCode: invoiceData.product_code,
                make: invoiceData.make,
                vin: invoiceData.vin,
                licenceNumber: invoiceData.license,
              });
              setStep(6);
              window.location = "/invoice";
            } else {
              Swal.fire("Unable to retrieve transaction");
            }
          })
          .catch((err) => {
            let message = "Unable to continue, please try again";
            if (err && err.response) {
              message = err.response.data.message;
            }
            Swal.fire(message);
          });
      };

      // ==========================================
      // RENDER LOGIC
      // ==========================================

      if (data.request_status === "Approved" && data.license_plate !== "") {
        return (
          <div>
            <button 
              onClick={handleViewCertificate}
              className="text-xs rounded-lg px-2 py-1 bg-green-700 text-green-100 hover:bg-green-800 transition-colors duration-300"
            >
              View Certificate
            </button>
          </div>
        );
      } else if (data.request_status === "Error") {
        return (
          <div>
            <button
              onClick={handleReview}
              className="text-xs rounded-lg py-1 px-2 bg-red-600 text-red-100 hover:bg-red-700 transition-colors duration-300"
            >
              Review
            </button>
          </div>
        );
      } else if (data.payment_status === "Unpaid") {
        return (
          <div className="flex space-x-4">
            <button
              onClick={handlePayment}
              className="text-xs rounded-lg px-2 py-1 bg-blue-500 text-blue-100 hover:bg-blue-600 transition-colors duration-300"
            >
              Make Payment
            </button>
            <button
              onClick={confirmPayment}
              className={`${
                confirmed ? "hidden" : ""
              } text-xs rounded-lg px-2 py-1 bg-green-500 text-green-100 hover:bg-green-600 transition-colors duration-300`}
            >
              {loading ? <Loader /> : "Confirm Payment"}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                Swal.fire({
                  title: "Are you sure?",
                  text: "Regenerating will create a new transaction reference. This action cannot be undone.",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonColor: "#3085d6",
                  cancelButtonColor: "#d33",
                  confirmButtonText: "Yes, regenerate it!"
                }).then((result) => {
                  if (result.isConfirmed) {
                    let product = getProductType(data.request_type);
                    RegenerateTransaction(e, product);
                  }
                });
              }}
              className={`${
                confirmed ? "hidden" : ""
              } text-xs rounded-lg px-2 py-1 bg-yellow-500 text-green-100 hover:bg-green-600 transition-colors duration-300`}
            >
              {regenerating ? <Loader /> : "Regenerate"}
            </button>
          </div>
        );
      } else if (data.request_status === "Processing") {
        return (
          <div>
            <button
              onClick={handleRequest}
              className="text-xs rounded-lg px-2 py-1 bg-gray-600 text-gray-100 hover:bg-gray-700 transition-colors duration-300"
            >
              View Request
            </button>
          </div>
        );
      } else if (data.request_status === "Approved" && data.license_plate === "") {
        return (
          <div>
            <button
              onClick={handleRequest}
              className="text-xs rounded-lg px-2 py-1 bg-green-700 text-green-100 hover:bg-green-800 transition-colors duration-300"
            >
              View Notification
            </button>
          </div>
        );
      } else {
        return (
          <div>
            <button 
              onClick={handleViewCertificate}
              className="text-xs rounded-lg py-1 px-2 bg-red-600 text-red-100 hover:bg-red-700 transition-colors duration-300"
            >
              View Certificate
            </button>
          </div>
        );
      }
    },
  },
];

export const MYSOLDVEHICLECOLUMNS = [
  {
    Header: "Chassis Number",
    accessor: "chassis_number",
  },
  {
    Header: "Make",
    accessor: "make",
  },
  {
    Header: "Sold to",
    accessor: "sold_to",
  },
  {
    Header: "Date Sold",
    accessor: "date_sold",
    Cell: ({ row }) => {
      const data = row.original;
      return <p>{formatDateNew(data.date_sold)}</p>;
    },
  },
];